// The side pane (Mitch, 2026-10-31: "make the complex simple and crystal clear"): the one place on the right where a
// record opens beside what you're working on, with its own editor, saving as you go. A BOM line, a part, a vendor: each
// kind of record has a Title and a Body (web/src/panes.tsx), and the pane gives them all the same frame.
// - It slides in, and its left edge drags to resize; each kind remembers its width in this browser (a double-click on
//   the edge goes back to the kind's own). The page keeps its left edge and gives up room on its right; when that would
//   leave it narrower than MIN_MAIN, the pane floats over the page's right side instead of squeezing it.
// - Opening something from inside it adds a level (PaneLink): the breadcrumb shows the trail, ← or Esc goes back one
//   level, a crumb back to it, × closes. Opening a record from the page starts the pane afresh (openPane's `root`).
// - The URL holds the trail (?pane=line:<id>,item:<id>), so a reload or a pasted link opens the same. Each open is a
//   browser history step that notes the trail before it (history.state.panePrev), so the browser's Back steps out of
//   the pane and Esc goes back the same way: back in history when that's where it leads, else by replacing the URL.
import { ArrowLeft, ExternalLink, X } from "lucide-react";
import { createContext, type MouseEvent, type ReactNode, type PointerEvent as ReactPointerEvent, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, navigate, setSearch, usePath, useSearch } from "../lib/router.tsx";
import { cx } from "./controls.tsx";
import { isTyping } from "./ListPage.tsx";

export type PaneEntry = { kind: string; id: string };
export type PaneKind = {
  /** Its width until someone drags it (px). */
  width: number;
  /** The short name in the breadcrumb ("MAT-ALPS-HAPTIC-01", "Line 3"). */
  Title: (p: { id: string }) => ReactNode;
  /** The editor. Inside the pane, RecordPanel drops its own frame and close button (useInPane()). */
  Body: (p: { id: string }) => ReactNode;
  /** Its own page, for "Open its full page" (a hook: it may read the record). */
  usePage?: (id: string) => string | null;
};

/** The page is never left narrower than this beside the pane: below it the pane floats over the page. */
const MIN_MAIN = 640;
const MIN_WIDTH = 340;
/** How long it slides (ms); the body stays until it's out. */
const SLIDE_MS = 200;

const read = (value: string | null): PaneEntry[] =>
  (value ?? "")
    .split(",")
    .map((s) => s.split(":"))
    .filter((p) => p.length === 2 && p[0] && p[1])
    .map(([kind, id]) => ({ kind, id }));
const write = (trail: PaneEntry[]) => trail.map((e) => `${e.kind}:${e.id}`).join(",");

/** The trail open in the side pane, outermost first. */
export function usePane(): PaneEntry[] {
  const value = useSearch().get("pane");
  return useMemo(() => read(value), [value]);
}

const current = () => new URLSearchParams(location.search).get("pane") ?? "";
function go(trail: PaneEntry[], replace: boolean) {
  const params = new URLSearchParams(location.search);
  const value = write(trail);
  value ? params.set("pane", value) : params.delete("pane");
  const qs = params.toString();
  // A replaced entry keeps its note: the entry before it is unchanged.
  navigate(location.pathname + (qs ? `?${qs}` : ""), { replace, state: replace ? history.state : { panePrev: current() } });
}

/** Opens a record in the pane: after `level` (a record opened from inside the pane), or afresh (`root`, or from the page).
 * `replace`: in place of what's open, not a history step (stepping through a table's rows with the pane open). */
export function openPane(entry: PaneEntry, at: { level?: number | null; root?: boolean; replace?: boolean } = {}) {
  const trail = read(current());
  const next = at.root || at.level == null ? [entry] : [...trail.slice(0, at.level + 1), entry];
  if (write(next) !== write(trail)) go(next, !!at.replace);
}

/** Back to the trail's first `keep` levels (0 closes the pane): back in history when that's where it was, else by
 * replacing the URL, so Esc and the browser's Back agree. */
export function paneBackTo(keep: number) {
  const trail = read(current());
  if (keep >= trail.length) return;
  const target = write(trail.slice(0, keep));
  if ((history.state as { panePrev?: string } | null)?.panePrev === target) history.back();
  else go(trail.slice(0, keep), true);
}

/** Back one level (a record's own close inside the pane). */
export const paneBack = () => paneBackTo(read(current()).length - 1);

/** The record of `kind` the pane opened first (a list lights its row), if any. */
export function usePaneRoot(kind: string | undefined): string | null {
  const trail = usePane();
  return kind && trail[0]?.kind === kind ? trail[0].id : null;
}

/** A page's own open-a-record param (?open=, ?bill=) opens the record in the pane instead, so every link, Ctrl+K and a
 * New dialog's "open what I made" keep working. Nothing without a kind. */
export function usePaneParam(param: string, kind: string | undefined) {
  const id = useSearch().get(param);
  useEffect(() => {
    if (!kind || !id) return;
    setSearch({ [param]: null });
    openPane({ kind, id }, { root: true, replace: true });
  }, [param, kind, id]);
}

/** The level a component is rendered in, inside the pane; null outside it. */
const PaneLevel = createContext<number | null>(null);
export const useInPane = () => useContext(PaneLevel) !== null;
/** The level it's rendered in (0 the first), null outside the pane: a body can look at the level that opened it. */
export const usePaneLevel = () => useContext(PaneLevel);

/** Where a pane's header goes: the pane's own title bar, beside the record's name (null while it's being drawn). */
const HeadSlot = createContext<HTMLElement | null>(null);

/**
 * A pane's header, the same for every pane (Mitch, 2026-11-04: "ensure they have a shared code module basis … info that
 * should be in the header is right below the header on sidebar views"): inside the side pane its status, key facts and
 * buttons go in the pane's title bar, after the record's name (its Title); anywhere else (a record's own page, a panel
 * beside a list) they're drawn where they are, as `fallback` wraps them.
 */
export function PaneHead({ children, fallback }: { children: ReactNode; fallback?: (children: ReactNode) => ReactNode }) {
  const slot = useContext(HeadSlot);
  const inPane = useInPane();
  if (inPane) return slot ? createPortal(children, slot) : null;
  return <>{fallback ? fallback(children) : children}</>;
}

/**
 * Opens a record from where the caller is rendered: inside the pane as the next level (`instead`: in place of the one
 * shown, a sibling like another variant or another member of its part group), from the page as the pane afresh.
 */
export function usePaneOpen() {
  const level = useContext(PaneLevel);
  return (kind: string, id: string, instead = false) =>
    level === null ? openPane({ kind, id }, { root: true }) : openPane({ kind, id }, { level: instead ? level - 1 : level });
}

/**
 * A link to a record (Mitch: any view opens "the relevant editor in the side" pane): inside the pane it opens the record
 * as the next level, from the page as the pane afresh. With Ctrl/⌘, Shift or a middle click it's a link to the record's
 * own page (`to`), as before.
 */
export function PaneLink({
  kind,
  id,
  to,
  className,
  title,
  children,
}: {
  kind: string;
  id: string;
  to: string;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  const level = useContext(PaneLevel);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation(); // a row's own click (opening its line or record) isn't this one
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openPane({ kind, id }, { level, root: level === null });
  };
  return (
    <Link to={to} onClick={onClick} className={className} title={title}>
      {children}
    </Link>
  );
}

const widthKey = (kind: string) => `hhl-ops.pane-width.${kind}`;
function readWidth(kind: string, fallback: number) {
  try {
    const n = Number(localStorage.getItem(widthKey(kind)));
    return n >= MIN_WIDTH ? n : fallback;
  } catch {
    return fallback;
  }
}
function writeWidth(kind: string, width: number | null) {
  try {
    width == null ? localStorage.removeItem(widthKey(kind)) : localStorage.setItem(widthKey(kind), String(Math.round(width)));
  } catch {
    // Blocked: it lasts the tab.
  }
}

/** The pane, beside the page (AppShell). `room`: the width the page and the pane share. */
export function SidePane({ kinds, room }: { kinds: Record<string, PaneKind>; room: number }) {
  const all = usePane();
  const trail = useMemo(() => all.filter((e) => kinds[e.kind]), [all, kinds]);
  const open = trail.length > 0;
  // The last trail shown, kept while the pane slides out so its body doesn't vanish first.
  const [shown, setShown] = useState(trail);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) {
      setShown(trail);
      // After a frame at width 0, so it slides; a window that isn't painting never gives one (a hidden or background
      // tab), so a moment later it opens anyway.
      const frame = requestAnimationFrame(() => setEntered(true));
      const anyway = setTimeout(() => setEntered(true), 100);
      return () => {
        cancelAnimationFrame(frame);
        clearTimeout(anyway);
      };
    }
    setEntered(false);
    const done = setTimeout(() => setShown([]), SLIDE_MS + 50);
    return () => clearTimeout(done);
  }, [open, trail]);

  const visible = open ? trail : shown;
  const top = visible[visible.length - 1];
  const kind = top ? kinds[top.kind] : undefined;
  const [dragged, setDragged] = useState<Record<string, number>>({});
  const [dragging, setDragging] = useState(false);
  const own = top && kind ? (dragged[top.kind] ?? readWidth(top.kind, kind.width)) : 0;
  const float = room - own < MIN_MAIN;
  const width = Math.max(MIN_WIDTH, Math.min(own, float ? room - 48 : room - MIN_MAIN));
  // The record shown, made once per record: a drag changes only the width around it, so it doesn't render on every move
  const level = visible.length - 1;
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const body = useMemo(
    () =>
      top &&
      kind && (
        <PaneLevel.Provider value={level}>
          <HeadSlot.Provider value={slot}>
            <kind.Body key={`${top.kind}:${top.id}`} id={top.id} />
          </HeadSlot.Provider>
        </PaneLevel.Provider>
      ),
    [top, kind, level, slot],
  );

  // Esc goes back one level: not while typing (a field's own Esc cancels its edit), over a dialog or a popover, or once
  // something closer took it (an open dropdown stops it).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || isTyping(e.target) || document.querySelector("[data-dialog], [data-popover]")) return;
      e.preventDefault();
      paneBackTo(trail.length - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, trail.length]);

  if (!top || !kind) return null;
  const drag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    setDragging(true);
    const [x0, w0] = [e.clientX, width];
    // Beside the page it stops where the page would get too narrow; floating, short of the page's left edge.
    const max = float ? room - 48 : room - MIN_MAIN;
    let last = w0;
    const move = (ev: PointerEvent) => {
      last = Math.max(MIN_WIDTH, Math.min(w0 - (ev.clientX - x0), max));
      setDragged((d) => ({ ...d, [top.kind]: last }));
    };
    const up = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", up);
      setDragging(false);
      writeWidth(top.kind, last);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", up);
  };
  const reset = () => {
    writeWidth(top.kind, null);
    setDragged(({ [top.kind]: _, ...rest }) => rest);
  };
  // No transition at all while dragging, so the edge stays under the pointer (without a duration it would still take
  // the default 150 ms, trailing behind every move)
  const slide = !dragging && cx(float ? "transition-transform" : "transition-[width]", "duration-200 ease-out motion-reduce:transition-none");
  const out = !(entered && open);

  return (
    <aside
      aria-label="Side pane"
      style={float ? { width, transform: out ? "translateX(100%)" : "none" } : { width: out ? 0 : width }}
      className={cx(
        "flex h-full shrink-0 overflow-hidden border-l border-line bg-surface",
        float ? "absolute inset-y-0 right-0 z-30 shadow-pop" : "relative",
        slide,
      )}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: a splitter to drag, not a thematic break */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the side pane"
        title="Drag to resize · double-click for its usual width"
        onPointerDown={drag}
        onDoubleClick={reset}
        className={cx("absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize hover:bg-accent/40", dragging && "bg-accent/60")}
      />
      <div style={{ width }} className="flex h-full min-w-0 shrink-0 flex-col">
        <div className="flex h-10 shrink-0 items-center gap-1 border-b border-line pr-2 pl-3 text-[12px]">
          {visible.length > 1 && (
            <button
              type="button"
              onClick={() => paneBackTo(visible.length - 1)}
              className="rounded p-1 text-muted hover:bg-hover hover:text-text"
              title="Back (Esc)"
            >
              <ArrowLeft className="size-4" />
            </button>
          )}
          <nav aria-label="Opened from" className="flex min-w-0 max-w-[60%] shrink items-center gap-1 overflow-hidden whitespace-nowrap">
            {visible.map((e, i) => {
              const K = kinds[e.kind];
              const last = i === visible.length - 1;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: a trail can hold the same record twice (a spec › its part › its PO › the spec)
                <span key={`${i}:${e.kind}:${e.id}`} className={cx("flex min-w-0 items-center gap-1", last ? "shrink" : "shrink-[2]")}>
                  {i > 0 && <span className="text-faint">›</span>}
                  {last ? (
                    <span className="truncate font-medium text-text">
                      <K.Title id={e.id} />
                    </span>
                  ) : (
                    <button type="button" onClick={() => paneBackTo(i + 1)} className="truncate text-muted hover:text-text hover:underline">
                      <K.Title id={e.id} />
                    </button>
                  )}
                </span>
              );
            })}
          </nav>
          {/* The record's header (PaneHead): its status, key facts and buttons, beside its name */}
          <div ref={setSlot} className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden pl-2" />
          {kind.usePage && <FullPage key={`${top.kind}:${top.id}`} usePage={kind.usePage} id={top.id} />}
          <button type="button" onClick={() => paneBackTo(0)} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close">
            <X className="size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
      </div>
    </aside>
  );
}

function FullPage({ usePage, id }: { usePage: (id: string) => string | null; id: string }) {
  const to = usePage(id);
  const path = usePath();
  // Its own page is the one we're on (a part opened from the Parts library): nothing more to open
  if (!to || to.split("?")[0] === path) return null;
  return (
    <Link to={to} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Open its full page">
      <ExternalLink className="size-4" />
    </Link>
  );
}

/** Whether the pane has anything open: a page's own Esc (a plan's detail) leaves it the first Esc. */
export const paneIsOpen = () => !!new URLSearchParams(location.search).get("pane");
